import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import {
  Bell, Heart, MessageCircle, UserPlus,
  Megaphone, Eye, Zap, Clock, Pin
} from "lucide-react";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import ProfileAvatarLink from "../components/profile/ProfileAvatarLink";
import ProfileNameLink from "../components/profile/ProfileNameLink";

const FIRESTORE_IN_LIMIT = 30;

function timeSince(ts) {
  if (!ts?.seconds) return "just now";
  const diff = Date.now() - ts.seconds * 1000;
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

const TYPE_CONFIG = {
  like:       { icon: Heart,        color: "#ec4899", bg: "#fdf2f8", label: "liked your moment" },
  comment:    { icon: MessageCircle,color: "#8b5cf6", bg: "#f3edff", label: "commented on your moment" },
  follow:     { icon: UserPlus,     color: "#22c55e", bg: "#f0fdf4", label: "started following you" },
  drop:       { icon: Zap,          color: "#f59e0b", bg: "#fffbeb", label: "scheduled a drop" },
  broadcast:  { icon: Megaphone,    color: "#f97316", bg: "#fff7ed", label: "sent a broadcast" },
  view_alert: { icon: Eye,          color: "#ef4444", bg: "#fef2f2", label: "almost gone" },
  default:    { icon: Bell,         color: "#8b5cf6", bg: "#f3edff", label: "new activity" },
};

function NotificationItem({ item }) {
  const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.default;
  const Icon = config.icon;
  const isBroadcast = item.type === "broadcast";
  const senderId = item.senderId || item.userId || item.creatorId;
  const senderName = item.creatorDisplayName || item.senderDisplayName || "Someone";
  const senderUser = {
    uid: senderId,
    id: senderId,
    isCreator: item.isCreator ?? Boolean(item.creatorDisplayName),
    handle: item.creatorHandle || item.senderHandle,
    username: item.creatorUsername || item.senderUsername,
  };

  return (
    <div
      className={`notif-item ${isBroadcast ? "notif-broadcast" : ""} ${!item.read ? "notif-unread" : ""}`}
    >
      <div className="notif-avatar-wrap">
        <ProfileAvatarLink
          user={senderUser}
          userId={senderId}
          isCreator={senderUser.isCreator}
          handle={senderUser.handle || senderUser.username}
          src={item.creatorAvatar || item.senderAvatar || ""}
          alt={senderName}
          className="notif-avatar-link"
          imgClassName="notif-avatar"
        />
        <div className="notif-type-icon" style={{ background: config.color }}>
          <Icon size={10} strokeWidth={2.5} color="white" />
        </div>
      </div>

      <Link to={item.link || "/"} className="notif-body">
        {isBroadcast && (
          <div className="notif-broadcast-badge">
            <Megaphone size={10} strokeWidth={2.5} />
            Creator broadcast
            {item.pinBroadcast && <><Pin size={9} strokeWidth={2.5} /> Pinned</>}
          </div>
        )}
        <p className="notif-text">
          <ProfileNameLink user={senderUser} className="notif-sender-name">
            {senderName}
          </ProfileNameLink>
          {" "}{item.text || config.label}
        </p>
        {isBroadcast && item.message && (
          <div className="notif-broadcast-message">
            "{item.message}"
          </div>
        )}
        <span className="notif-time">
          <Clock size={11} strokeWidth={2} />
          {timeSince(item.createdAt)}
        </span>
      </Link>

      {!item.read && <div className="notif-dot" />}
    </div>
  );
}

function BroadcastItem({ broadcast }) {
  const creatorUser = {
    handle: broadcast.creatorUsername,
    username: broadcast.creatorUsername,
    isCreator: true,
  };

  return (
    <div className="notif-item notif-broadcast">
      <div className="notif-avatar-wrap">
        <ProfileAvatarLink
          user={creatorUser}
          isCreator
          handle={broadcast.creatorUsername}
          src={broadcast.creatorAvatar || ""}
          alt={broadcast.creatorDisplayName}
          className="notif-avatar-link"
          imgClassName="notif-avatar"
        />
        <div className="notif-type-icon" style={{ background: "#f97316" }}>
          <Megaphone size={10} strokeWidth={2.5} color="white" />
        </div>
      </div>

      <Link to={`/creator/${broadcast.creatorUsername}`} className="notif-body">
        <div className="notif-broadcast-badge">
          <Megaphone size={10} strokeWidth={2.5} />
          Creator broadcast
          {broadcast.pinBroadcast && (
            <span className="notif-pinned-tag">
              <Pin size={9} strokeWidth={2.5} /> Pinned
            </span>
          )}
        </div>
        <p className="notif-text">
          <ProfileNameLink user={creatorUser} className="notif-sender-name">
            {broadcast.creatorDisplayName}
          </ProfileNameLink>{" "}
          sent an update
        </p>
        <div className="notif-broadcast-message">
          &ldquo;{broadcast.message || broadcast.text || ""}&rdquo;
        </div>
        <span className="notif-time">
          <Clock size={11} strokeWidth={2} />
          {timeSince(broadcast.createdAt)}
        </span>
      </Link>
    </div>
  );
}

export default function Notifications() {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    const load = async () => {
      if (!currentUser) { setLoading(false); return; }
      try {
        const [legacySnap, inboxSnap, followSnap, supportSnap] = await Promise.all([
          getDocs(query(
            collection(db, "notifications"),
            where("userId", "==", currentUser.uid)
          )),
          getDocs(collection(db, "notifications", currentUser.uid, "items")),
          getDocs(query(
            collection(db, "follows"),
            where("followerId", "==", currentUser.uid)
          )),
          getDocs(query(
            collection(db, "supporters"),
            where("userId", "==", currentUser.uid)
          )),
        ]);

        const inboxItems = inboxSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          _source: "inbox",
        }));

        const legacyNotifs = legacySnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          _source: "legacy",
        }));

        const inboxBroadcasts = inboxItems
          .filter((n) => n.type === "broadcast")
          .map((n) => ({
            ...n,
            type: "broadcast",
            message: n.message || n.text,
          }));

        const activity = [
          ...legacyNotifs.filter((n) => n.type !== "broadcast"),
          ...inboxItems.filter((n) => n.type !== "broadcast"),
        ].sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setNotifications(activity);

        const followedIds = followSnap.docs.map((d) => d.data().creatorId).filter(Boolean);
        const subscribedIds = supportSnap.docs
          .map((d) => d.data())
          .filter((s) => !s.status || s.status === "active")
          .map((s) => s.creatorId)
          .filter(Boolean);
        const creatorIds = [...new Set([...followedIds, ...subscribedIds])].slice(0, FIRESTORE_IN_LIMIT);

        const byId = new Map();
        inboxBroadcasts.forEach((b) => {
          byId.set(b.broadcastId || b.id, b);
        });

        if (creatorIds.length > 0) {
          const broadcastSnap = await getDocs(query(
            collection(db, "broadcasts"),
            where("creatorId", "in", creatorIds)
          ));
          broadcastSnap.docs.forEach((d) => {
            const data = { id: d.id, type: "broadcast", ...d.data() };
            byId.set(d.id, data);
          });
        }

        const broadcastData = [...byId.values()]
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setBroadcasts(broadcastData);
      } catch (err) {
        console.error("Notifications load error:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser]);

  if (!currentUser) return <Navigate to="/welcome" replace />;

  // Merge and sort all items for "all" tab
  const allItems = [...notifications, ...broadcasts]
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

  const unread = notifications.filter(n => !n.read);

  const tabs = [
    { id: "all",        label: "All",        count: allItems.length },
    { id: "broadcasts", label: "Broadcasts", count: broadcasts.length },
    { id: "activity",   label: "Activity",   count: notifications.length },
  ];

  const displayItems = activeTab === "all" ? allItems
    : activeTab === "broadcasts" ? broadcasts
    : notifications;

  return (
    <div className="notif-page">

      {/* Header */}
      <div className="notif-header">
        <div className="notif-header-icon">
          <Bell size={22} color="white" strokeWidth={2} />
        </div>
        <div>
          <p className="exact-eyebrow">Activity</p>
          <h1>Notifications</h1>
          <p className="notif-header-sub">
            Creator broadcasts, drop alerts, and platform activity.
          </p>
        </div>
        {unread.length > 0 && (
          <div className="notif-unread-count">
            {unread.length} unread
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="notif-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            className={`notif-tab ${activeTab === tab.id ? "notif-tab-active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className="notif-tab-count">{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="notif-loading">Loading notifications...</div>
      ) : displayItems.length === 0 ? (
        <div className="notif-empty">
          <Bell size={32} strokeWidth={1.5} color="var(--light-muted)" />
          <strong>
            {activeTab === "broadcasts" ? "No broadcasts yet" :
             activeTab === "activity" ? "No activity yet" :
             "Nothing yet"}
          </strong>
          <p>
            {activeTab === "broadcasts"
              ? "Subscribe to creators to get their broadcasts here."
              : "Follow creators to get drop alerts and updates."}
          </p>
        </div>
      ) : (
        <div className="notif-list">
          {displayItems.map(item =>
            item.type === "broadcast"
              ? <BroadcastItem key={item.id} broadcast={item} />
              : <NotificationItem key={item.id} item={item} />
          )}
        </div>
      )}
    </div>
  );
}
